import { useEffect, useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { AddImageModal } from '@components/add-image/add-image';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { FormTextarea } from '@components/form/form-textarea';
import { IconDate } from '@components/icons/date';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconMessages } from '@components/icons/messages';
import { IconNumberCounter } from '@components/icons/number-counter';
import { IconPerson } from '@components/icons/person';
import { IconPlay } from '@components/icons/play';
import { IconReplace } from '@components/icons/replace';
import { InfoBox } from '@components/info-box';
import { Spinner } from '@components/loading-spinner';
import { useModal } from '@components/modal';
import { NumberField } from '@components/number-field';
import { PageContainer } from '@components/page-containers';
import { PortalCard } from '@components/portal-card';
import { SelectMulti } from '@components/selectmulti';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { timeOptions15Min } from '@utils/hoursOptions';

import {
  usePortalsUsingJourneyForm,
  useUpdateJourneyFormPortalInfo,
} from 'api/journey-forms/hooks';
import { JourneyForm, SSOPrefillMode } from 'api/journey-forms/requests';
import {
  useAddJourneyFormToPortal,
  usePortals,
  useRemoveJourneyFormFromPortal,
} from 'api/portals/hooks';

import { SsoPrefillSection } from './sso-prefill-section';

export const JourneyFormSettingsPage = ({
  form,
  isAutoStartOnChange,
  portalOnChange,
  onUpdate,
}: {
  form: JourneyForm;
  isAutoStartOnChange: (isAutoStart: boolean) => void;
  portalOnChange: (isPortalForm: boolean) => void;
  onUpdate: (updates: Partial<JourneyForm>) => void;
}) => {
  return (
    <Container>
      <PageContainer.Page>
        <PageContainer.PageContent>
          <CenteredContent>
            <Stack space={6}>
              <FormSubmission isAutoStart={form.isAutoStart} onChange={isAutoStartOnChange} />
              <Separator />
              <AccessScheduling form={form} onUpdate={onUpdate} />
              <Separator />
              <Portal form={form} onChange={portalOnChange} />
            </Stack>
          </CenteredContent>
        </PageContainer.PageContent>
      </PageContainer.Page>
    </Container>
  );
};

const FormSubmission = ({
  isAutoStart,
  onChange,
}: {
  isAutoStart?: JourneyForm['isAutoStart'];
  onChange: (isAutoStart: boolean) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <Stack space={2}>
        <Text text={t('formSubmission')} font="bold18" />
        <Text text={t('formSubmissionInfo')} font="regular14" color="textSecondary" />
      </Stack>
      <Card variant="transparent" space={4}>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack space={2} orientation="horizontal" align="center">
            <SettingIcon>
              <IconPlay size={20} color="textSecondary" />
            </SettingIcon>
            <Text text={t('autoSwitchTitle')} font="semibold14" />
          </Stack>
          <Switch
            label={t('autoSwitchTitle')}
            defaultValue={Boolean(isAutoStart)}
            labelVisible={false}
            onChange={onChange}
          />
        </Stack>
      </Card>
    </Stack>
  );
};

type CloseAtFormProps = {
  closeDate: Date;
  closeTime: string;
};

const CloseAtForm = createForm<CloseAtFormProps>();

type ClosedMessageFormProps = {
  closedHeading: string;
  closedMessage: string;
};

const ClosedMessageForm = createForm<ClosedMessageFormProps>();

const AccessScheduling = ({
  form,
  onUpdate,
}: {
  form: JourneyForm;
  onUpdate: (updates: Partial<JourneyForm>) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const [closeAtEnabled, setCloseAtEnabled] = useState(Boolean(form.closeAt));
  const [responseLimitEnabled, setResponseLimitEnabled] = useState(Boolean(form.responseLimit));
  const [closedMessageEnabled, setClosedMessageEnabled] = useState(
    Boolean(form.closedHeading || form.closedMessage)
  );
  const [responseLimitDraft, setResponseLimitDraft] = useState<number | undefined>(
    form.responseLimit ?? undefined
  );
  const [responseLimitResetKey, setResponseLimitResetKey] = useState(0);

  const isResponseLimitDirty = responseLimitDraft !== (form.responseLimit ?? undefined);
  const showResponseLimitButtons = form.responseLimit === null || isResponseLimitDirty;

  const closeAtFormStore = useCreateFormStore();
  const isCloseAtDirty = closeAtFormStore.useIsDirty();
  const showCloseAtButtons = !form.closeAt || isCloseAtDirty;

  const closedMessageFormStore = useCreateFormStore();
  const isClosedMessageDirty = closedMessageFormStore.useIsDirty();
  const showClosedMessageButtons =
    (!form.closedHeading && !form.closedMessage) || isClosedMessageDirty;

  const existingCloseAt = useMemo(
    () => (form.closeAt ? new Date(form.closeAt) : null),
    [form.closeAt]
  );

  const defaultCloseDate = useMemo(() => {
    if (existingCloseAt) return existingCloseAt;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }, [existingCloseAt]);

  const handleCloseAtSubmit = (values: CloseAtFormProps) => {
    const combined = new Date(values.closeDate);
    const [hours, minutes] = values.closeTime.split(':').map(Number);
    combined.setHours(hours, minutes, 0, 0);
    onUpdate({ closeAt: combined.toISOString() });
  };

  const handleCloseAtToggle = (enabled: boolean) => {
    setCloseAtEnabled(enabled);
    if (!enabled) {
      onUpdate({ closeAt: null });
    }
  };

  const handleResponseLimitToggle = (enabled: boolean) => {
    setResponseLimitEnabled(enabled);
    if (!enabled) {
      onUpdate({ responseLimit: null });
      setResponseLimitDraft(undefined);
    }
  };

  const handleResponseLimitSave = () => {
    onUpdate({ responseLimit: responseLimitDraft ?? null });
  };

  const handleResponseLimitCancel = () => {
    setResponseLimitDraft(form.responseLimit ?? undefined);
    setResponseLimitResetKey((k) => k + 1);
    if (form.responseLimit === null) {
      setResponseLimitEnabled(false);
    }
  };

  const handleClosedMessageToggle = (enabled: boolean) => {
    setClosedMessageEnabled(enabled);
    if (!enabled) {
      onUpdate({ closedHeading: '', closedMessage: '' });
    }
  };

  const handleClosedMessageSubmit = (values: ClosedMessageFormProps) => {
    onUpdate({
      closedHeading: values.closedHeading || '',
      closedMessage: values.closedMessage || '',
    });
  };

  return (
    <Stack space={4}>
      <Stack space={2}>
        <Text text={t('accessScheduling')} font="bold18" />
        <Text text={t('accessSchedulingInfo')} font="regular14" color="textSecondary" />
      </Stack>
      <Card variant="transparent" space={4}>
        <Stack space={2}>
          <Stack orientation="horizontal" align="center" justify="space-between">
            <Stack space={2} orientation="horizontal" align="center">
              <SettingIcon>
                <IconDate size={20} color="textSecondary" />
              </SettingIcon>
              <Text text={t('scheduleCloseDate')} font="semibold14" />
            </Stack>
            <Switch
              label={t('scheduleCloseDate')}
              value={closeAtEnabled}
              labelVisible={false}
              onChange={handleCloseAtToggle}
            />
          </Stack>
          <AnimatePresence>
            {closeAtEnabled && (
              <AnimatedDiv>
                <CloseAtForm onSubmit={handleCloseAtSubmit} store={closeAtFormStore}>
                  <Stack space={2}>
                    <DateTimeSelectContainer>
                      <CloseAtForm.DatePicker
                        id="closeDate"
                        label={t('date')}
                        defaultValue={defaultCloseDate}
                        validators={['required']}
                      />
                      <CloseAtForm.Select
                        id="closeTime"
                        label={t('time')}
                        options={timeOptions15Min}
                        defaultValue={
                          existingCloseAt
                            ? `${existingCloseAt
                                .getHours()
                                .toString()
                                .padStart(2, '0')}:${existingCloseAt
                                .getMinutes()
                                .toString()
                                .padStart(2, '0')}`
                            : '09:00'
                        }
                        isClearable={false}
                        validators={['required']}
                      />
                    </DateTimeSelectContainer>
                    {showCloseAtButtons && (
                      <CloseAtForm.Actions>
                        <AnimatePresence>
                          <AnimatedDiv>
                            <Stack orientation="horizontal" space={4} justify="flex-end">
                              <Button
                                text={t('core.cancel')}
                                variant="secondary"
                                onClick={() => {
                                  closeAtFormStore.actions.resetStore();
                                  if (!form.closeAt) {
                                    setCloseAtEnabled(false);
                                  }
                                }}
                              />
                              <CloseAtForm.Submit text={t('core.save')} variant="primary-blue" />
                            </Stack>
                          </AnimatedDiv>
                        </AnimatePresence>
                      </CloseAtForm.Actions>
                    )}
                  </Stack>
                </CloseAtForm>
              </AnimatedDiv>
            )}
          </AnimatePresence>
        </Stack>
      </Card>

      <Card variant="transparent" space={4}>
        <Stack space={2}>
          <Stack orientation="horizontal" align="center" justify="space-between">
            <Stack space={2} orientation="horizontal" align="center">
              <SettingIcon>
                <IconPerson size={20} color="textSecondary" />
              </SettingIcon>
              <Text text={t('preventDuplicateSubmissions')} font="semibold14" />
            </Stack>
            <Switch
              label={t('preventDuplicateSubmissions')}
              defaultValue={!form.allowDuplicateTravellers}
              labelVisible={false}
              onChange={(value) => onUpdate({ allowDuplicateTravellers: !value })}
            />
          </Stack>
          <AnimatePresence>
            {!form.allowDuplicateTravellers && (
              <AnimatedDiv>
                <Text
                  text={t('duplicateSubmissionHelpText')}
                  font="regular14"
                  color="textSecondary"
                />
              </AnimatedDiv>
            )}
          </AnimatePresence>
        </Stack>
      </Card>
      <Card variant="transparent" space={4}>
        <Stack space={2}>
          <Stack orientation="horizontal" align="center" justify="space-between">
            <Stack space={2} orientation="horizontal" align="center">
              <SettingIcon>
                <IconNumberCounter size={20} color="textSecondary" />
              </SettingIcon>
              <Text text={t('setResponseLimit')} font="semibold14" />
            </Stack>
            <Switch
              label={t('setResponseLimit')}
              value={responseLimitEnabled}
              labelVisible={false}
              onChange={handleResponseLimitToggle}
            />
          </Stack>
          <AnimatePresence>
            {responseLimitEnabled && (
              <AnimatedDiv>
                <Stack space={3}>
                  <Text text={t('responseLimitHelpText')} font="regular14" color="textSecondary" />
                  <div style={{ width: '50%' }}>
                    <NumberField
                      key={`${form.responseLimit}-${responseLimitResetKey}`}
                      label={t('responseLimit')}
                      defaultValue={responseLimitDraft}
                      placeholder={t('responseLimitPlaceholder')}
                      onChange={(value) => setResponseLimitDraft(value)}
                      error={
                        responseLimitDraft !== undefined && responseLimitDraft < 1
                          ? t('responseLimitMinError')
                          : undefined
                      }
                    />
                  </div>
                  <AnimatePresence>
                    {showResponseLimitButtons && (
                      <AnimatedDiv>
                        <Stack orientation="horizontal" space={4} justify="flex-end">
                          <Button
                            text={t('core.cancel')}
                            variant="secondary"
                            onClick={handleResponseLimitCancel}
                          />
                          <Button
                            text={t('core.save')}
                            variant="primary-blue"
                            onClick={handleResponseLimitSave}
                            isDisabled={responseLimitDraft === undefined || responseLimitDraft < 1}
                          />
                        </Stack>
                      </AnimatedDiv>
                    )}
                  </AnimatePresence>
                </Stack>
              </AnimatedDiv>
            )}
          </AnimatePresence>
        </Stack>
      </Card>

      {(closeAtEnabled || responseLimitEnabled) && (
        <Card variant="transparent" space={4}>
          <Stack space={2}>
            <Stack orientation="horizontal" align="center" justify="space-between">
              <Stack space={2} orientation="horizontal" align="center">
                <SettingIcon>
                  <IconMessages size={20} color="textSecondary" />
                </SettingIcon>
                <Text font="semibold14">{t('showClosedMessage')}</Text>
              </Stack>
              <Switch
                label={t('showClosedMessage')}
                value={closedMessageEnabled}
                labelVisible={false}
                onChange={handleClosedMessageToggle}
              />
            </Stack>
            <AnimatePresence>
              {closedMessageEnabled && (
                <AnimatedDiv>
                  <ClosedMessageForm
                    onSubmit={handleClosedMessageSubmit}
                    store={closedMessageFormStore}
                  >
                    <Stack space={3}>
                      <Stack space={1}>
                        <Text
                          text={t('closedMessageHelpText')}
                          font="regular14"
                          color="textSecondary"
                        />
                        <Text text={t('messageShownWhen')} font="regular14" color="textSecondary" />
                        <Text
                          text={`• ${t('deadlinePassedCondition')}`}
                          font="regular14"
                          color="textSecondary"
                        />
                        <Text
                          text={`• ${t('responseLimitCondition')}`}
                          font="regular14"
                          color="textSecondary"
                        />
                        <Text
                          text={t('whichEverHappensFirst')}
                          font="regular14"
                          color="textSecondary"
                        />
                      </Stack>
                      <ClosedMessageForm.TextField
                        id="closedHeading"
                        label={t('closedHeading')}
                        defaultValue={form.closedHeading ?? ''}
                        placeholder={t('closedHeadingPlaceholder')}
                        maxChars={100}
                        validators={['required']}
                      />
                      <FormTextarea<ClosedMessageFormProps>
                        id="closedMessage"
                        label={t('closedMessage')}
                        defaultValue={form.closedMessage ?? ''}
                        placeholder={t('closedMessagePlaceholder')}
                        maxLength={250}
                        validators={['required']}
                      />
                      {showClosedMessageButtons && (
                        <ClosedMessageForm.Actions>
                          <AnimatePresence>
                            <AnimatedDiv>
                              <Stack orientation="horizontal" space={4} justify="flex-end">
                                <Button
                                  text={t('core.cancel')}
                                  variant="secondary"
                                  onClick={() => {
                                    closedMessageFormStore.actions.resetStore();
                                    if (!form.closedHeading && !form.closedMessage) {
                                      setClosedMessageEnabled(false);
                                    }
                                  }}
                                />
                                <ClosedMessageForm.Submit
                                  text={t('core.save')}
                                  variant="primary-blue"
                                />
                              </Stack>
                            </AnimatedDiv>
                          </AnimatePresence>
                        </ClosedMessageForm.Actions>
                      )}
                    </Stack>
                  </ClosedMessageForm>
                </AnimatedDiv>
              )}
            </AnimatePresence>
          </Stack>
        </Card>
      )}
    </Stack>
  );
};

type PortalFormProps = {
  formTitle: string;
  formPortalDescription: string;
};

const Form = createForm<PortalFormProps>();

const Portal = ({
  form,
  onChange,
}: {
  form: JourneyForm;
  onChange: (isPortalForm: boolean) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { data: portals, isLoading: isPortalsLoading } = usePortals();
  const { data: portalsInUseOptions, isLoading: isPortalsInUseLoading } =
    usePortalsUsingJourneyForm(form?.shortId ?? '');

  const addJourneyFormToPortal = useAddJourneyFormToPortal();
  const removeJourneyFormFromPortal = useRemoveJourneyFormFromPortal();

  const [image, setImage] = useState<File | undefined>(undefined);
  const updatePortalInfoMutation = useUpdateJourneyFormPortalInfo({
    journeyShortId: form?.journey ?? '',
    journeyFormId: form?.shortId ?? '',
    onSuccess: () => {
      setImage(undefined);
    },
  });
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isDirty = formStore.useIsDirty();
  const formTitle = formValues['formTitle'] as string;
  const formDescription = formValues['formPortalDescription'] as string;
  const showModal = useModal(AddImageModal, { variant: 'LARGE' });

  const [selectedPortals, setSelectedPortals] = useState<any[]>([]);

  useEffect(() => {
    if (Array.isArray(portalsInUseOptions)) {
      setSelectedPortals(portalsInUseOptions);
    }
  }, [portalsInUseOptions]);

  // Reset the form store, if the form name changes
  // because that means the user updated the name in the corner
  useEffect(() => {
    formStore.actions.resetStore();
    setImage(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name]);

  const handleSubmit = async (values: PortalFormProps) => {
    const { formTitle, formPortalDescription } = values;
    const data = {
      name: formTitle ?? form.name,
      portalDescription: formPortalDescription,
      ...(image && { portalImage: image }),
    };
    await updatePortalInfoMutation.mutateAsync(data);
  };

  const handlePortalSelectionChange = async (selectedOptions: any[]) => {
    const newSelection = selectedOptions.map((option) => option.value);
    const currentSelection = selectedPortals.map((p) => p.value);

    const addedPortals = newSelection.filter((portalId) => !currentSelection.includes(portalId));
    const removedPortals = currentSelection.filter((portalId) => !newSelection.includes(portalId));

    setSelectedPortals(selectedOptions);

    for (const portalId of addedPortals) {
      await addJourneyFormToPortal.mutateAsync({
        portalShortId: portalId,
        form: form.shortId,
      });
    }

    for (const portalId of removedPortals) {
      await removeJourneyFormFromPortal.mutateAsync({
        portalShortId: portalId,
        formShortId: form.shortId,
        useJourneyFormId: true,
      });
    }
  };

  const isEditing = isDirty || image !== undefined;

  const portalImage = image ? URL.createObjectURL(image) : form?.portalImageUrl;

  const portalOptions =
    portals?.map((portal) => ({
      label: portal.name,
      value: portal.shortId,
    })) ?? [];

  const portalsInUseShortIds = new Set(selectedPortals.map((option) => option.value));
  const hasAnySsoPortal = Boolean(
    portals?.some(
      (portal) => portalsInUseShortIds.has(portal.shortId) && portal.securityType === 'azure_sso'
    )
  );

  const handleSsoPrefillModeChange = async (nextMode: SSOPrefillMode) => {
    await updatePortalInfoMutation.mutateAsync({
      name: form.name,
      portalDescription: form.portalDescription ?? '',
      ssoPrefillMode: nextMode,
    });
  };

  if (isPortalsLoading || isPortalsInUseLoading) {
    return <Spinner />;
  }

  return (
    <Form onSubmit={handleSubmit} store={formStore}>
      <Stack space={6} style={{ width: pxToRem(680) }}>
        <Stack space={2}>
          <Text text={t('portal')} font="bold18" />
          <Text text={t('portalInfo')} font="regular14" color="textSecondary" />
          <SelectMulti
            label={t('showOnPortals')}
            options={portalOptions}
            value={selectedPortals}
            onChange={handlePortalSelectionChange}
          />
        </Stack>

        {!form?.isPublished && (
          <InfoBox
            variant="indigo"
            text={t('infoBoxText')}
            icon={<IconFormIndicatorInfo color="indigo100" />}
          />
        )}

        <Card variant="transparent" space={6}>
          <Stack space={6} orientation="horizontal" justify="space-between">
            <Stack space={4} style={{ width: '100%' }}>
              <Form.TextField
                id="formTitle"
                label={t('formTitle')}
                validators={['required']}
                defaultValue={form.name}
              />
              <Form.TextField
                id="formPortalDescription"
                label={t('description')}
                defaultValue={form.portalDescription ?? form.description}
                placeholder={t('descrptionPlaceholderText')}
              />
              <Button
                variant="secondary"
                onClick={() => showModal({ onChange: setImage })}
                text={t('replaceImage')}
                iconLeft={<IconReplace />}
              />
              {isEditing && (
                <Form.Actions>
                  <AnimatePresence>
                    <AnimatedDiv>
                      <Stack orientation="horizontal" space={4} justify="flex-end">
                        <Button
                          text={t('core.cancel')}
                          variant="secondary"
                          onClick={() => {
                            formStore.actions.resetStore();
                            setImage(undefined);
                          }}
                        />
                        <Form.Submit text={t('core.save')} variant="primary-blue" />
                      </Stack>
                    </AnimatedDiv>
                  </AnimatePresence>
                </Form.Actions>
              )}
            </Stack>
            <div style={{ width: '100%' }}>
              <PortalCard name={formTitle} description={formDescription} image={portalImage} />
            </div>
          </Stack>
        </Card>

        {hasAnySsoPortal && (
          <SsoPrefillSection
            currentMode={form.ssoPrefillMode}
            onChange={handleSsoPrefillModeChange}
          />
        )}
      </Stack>
    </Form>
  );
};

const AnimatedDiv = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div
      transition={{ duration: 0.2 }}
      animate={{ opacity: 1, height: 'auto' }}
      initial={{ opacity: 0, height: 0 }}
      exit={{ opacity: 0, height: 0 }}
    >
      {children}
    </motion.div>
  );
};

const Container = styled.div({
  background: theme.color.backgroundPrimary,
});

const CenteredContent = styled.div({
  display: 'flex',
  justifyContent: 'center',
});

const DateTimeSelectContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 4,
})({
  '& > *': {
    flex: 1,
  },
});

const SettingIcon = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const locale = {
  en: {
    formSubmission: 'Form submission',
    formSubmissionInfo:
      'When enabled, the journey workflow will start automatically on form submission',
    autoSwitchTitle: 'Start traveller journey on form submission',
    accessScheduling: 'Access & scheduling',
    accessSchedulingInfo: 'Control when and how this form accepts submissions',
    scheduleCloseDate: 'Schedule a close date',
    date: 'Date',
    time: 'Time',
    setResponseLimit: 'Set a total submission limit',
    responseLimit: 'Submission limit',
    responseLimitPlaceholder: 'Maximum number of submissions',
    responseLimitHelpText: 'The form will close once you collect the limit you apply',
    responseLimitMinError: 'The number of submissions must be greater than 0',
    preventDuplicateSubmissions: 'Only allow one submission per traveller',
    duplicateSubmissionHelpText: 'Submissions are restricted to one per email address',
    showClosedMessage: 'Show a custom close message',
    closedMessageHelpText:
      'Customize the message users will see when this form is no longer accepting responses.',
    messageShownWhen: '\nThis message is shown when:',
    deadlinePassedCondition: 'The submission deadline has passed, or',
    responseLimitCondition: 'The total submission limit has been reached',
    whichEverHappensFirst: '(whichever happens first if both are enabled)',
    closedHeading: 'Heading',
    closedHeadingPlaceholder: 'Enter a heading for the closed message',
    closedMessage: 'Closed message',
    closedMessagePlaceholder: 'Message to show when the form is closed',
    portal: 'Portal',
    portalInfo:
      'When enabled, forms are displayed and accessible to anyone with access from your company portal',
    replaceImage: 'Replace image',
    formTitle: 'Portal form title',
    description: 'Description',
    descrptionPlaceholderText: 'Description for this form',
    infoBoxText:
      'This form is currently in-active, re-activate this form to display it on the Portal',
    showOnPortals: 'Show on portals',
  },
};
